import { ReactNode } from 'react'
import {
  FloatingPortal,
  FloatingOverlay,
  useDismiss,
  useFloating,
  useInteractions,
  useTransitionStatus,
} from '@floating-ui/react'
import styles from './SideSheet.module.scss'
import { ISideSheetProps } from './types'
import clsx from 'clsx'

export interface SideSheetComponent {
  (props: ISideSheetProps): JSX.Element | null
  Header: (props: { children?: ReactNode }) => JSX.Element
  Body: (props: { children?: ReactNode }) => JSX.Element
  Footer: (props: { children?: ReactNode }) => JSX.Element
}

const TRANSITION_DURATION = 300

export const SideSheet: SideSheetComponent = ({
  isOpen,
  placement = 'right',
  children,
  closeOnClickOutside = true,
  onChange,
  hasOverlay = true,
}: ISideSheetProps): JSX.Element | null => {
  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: onChange,
    placement,
  })

  const dismiss = useDismiss(context, {
    outsidePress: closeOnClickOutside,
  })

  const { getFloatingProps } = useInteractions([dismiss])

  const { isMounted, status } = useTransitionStatus(context, {
    duration: TRANSITION_DURATION,
  })

  if (!isMounted) return null

  return (
    <FloatingPortal>
      <FloatingOverlay
        lockScroll
        className={clsx(styles.sideSheet, {
          [styles.withOverlay]: hasOverlay,
          [styles.entering]: status === 'open',
          [styles.leaving]: status === 'close',
        })}
      >
        <div
          ref={refs.setFloating}
          className={clsx(styles.sideSheetContent, styles[placement], styles[status])}
          {...getFloatingProps()}
        >
          {children}
        </div>
      </FloatingOverlay>
    </FloatingPortal>
  )
}

SideSheet.Header = function SideSheetHeader({ children }: { children?: ReactNode }): JSX.Element {
  return <div className={styles.sideSheetHeader}>{children}</div>
}

SideSheet.Body = function SideSheetBody({ children }: { children?: ReactNode }): JSX.Element {
  return <div className={styles.sideSheetBody}>{children}</div>
}

SideSheet.Footer = function SideSheetFooter({ children }: { children?: ReactNode }): JSX.Element {
  return <div className={styles.sideSheetFooter}>{children}</div>
}

export default SideSheet
